import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import NavBar from "../partials/NavBar";
import { GlobalContext } from "../GlobalContext";

const LIMIT = 100;
const FIELD_OPTS = [
  ["all", "All fields"],
  ["en", "English"],
  ["kr", "Korean"],
  ["ru", "Russian"],
];
const LANG_NAME = { en: "English", kr: "Korean", ru: "Russian" };
const emptyTerm = () => ({ id: null, term: "", kr: "", ru: "", krAltsText: "", ruAltsText: "" });
// Alternate phrasings are edited as one-per-line text; rows carry them as arrays.
const toEdit = (row) => ({
  ...row,
  krAltsText: (row.krAlts || []).join("\n"),
  ruAltsText: (row.ruAlts || []).join("\n"),
});
const splitLines = (s) =>
  String(s || "")
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);

function Terms() {
  const { username, logout, updateDatabase, toast, socket, isSocketReady } =
    useContext(GlobalContext);

  const [tab, setTab] = useState("terms");
  const toastId = useRef(null);
  const tmToast = useCallback(
    (kind, msg) => {
      const fn = kind === "error" ? toast.error : kind === "success" ? toast.success : toast.info;
      if (toastId.current != null) toast.dismiss(toastId.current);
      toastId.current = fn(msg, { position: "bottom-right", autoClose: 2500 });
    },
    [toast]
  );

  // Promise wrapper over an ack-based socket event with a timeout.
  const call = useCallback(
    (event, payload) =>
      new Promise((resolve) => {
        if (!socket || !isSocketReady) {
          resolve({ error: "Server offline." });
          return;
        }
        let done = false;
        const timer = setTimeout(() => {
          if (!done) {
            done = true;
            resolve({ error: "Timed out waiting for the server." });
          }
        }, 20000);
        socket.emit(event, payload, (resp) => {
          if (done) return;
          done = true;
          clearTimeout(timer);
          resolve(resp || { error: "No response from the server." });
        });
      }),
    [socket, isSocketReady]
  );

  // ---------------- Terms tab ----------------
  const [q, setQ] = useState("");
  const [field, setField] = useState("all");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // {id|null, term, kr, ru}
  const reqRef = useRef(0);
  const savingRef = useRef(false); // guards double Enter / double-click on Save

  const search = useCallback(
    async (skip = 0, append = false) => {
      const my = ++reqRef.current;
      setLoading(true);
      const resp = await call("terms:search", {
        q,
        field: field === "all" ? undefined : field,
        skip,
        limit: LIMIT,
      });
      if (my !== reqRef.current) return; // superseded by a newer search
      setLoading(false);
      if (resp.error) {
        tmToast("error", resp.error);
        return;
      }
      setItems((prev) => (append ? [...prev, ...resp.items] : resp.items));
      setTotal(resp.total);
    },
    [call, q, field, tmToast]
  );

  useEffect(() => {
    if (tab !== "terms" || !isSocketReady) return undefined;
    const t = setTimeout(() => search(0, false), 250);
    return () => clearTimeout(t);
  }, [q, field, tab, isSocketReady, search]);

  const saveEdit = async () => {
    if (!editing || savingRef.current) return;
    savingRef.current = true;
    const resp = await call("terms:save", {
      id: editing.id,
      term: editing.term,
      kr: editing.kr,
      ru: editing.ru,
      krAlts: splitLines(editing.krAltsText),
      ruAlts: splitLines(editing.ruAltsText),
    });
    savingRef.current = false;
    if (resp.error) {
      tmToast("error", resp.error);
      return;
    }
    tmToast(
      "success",
      resp.item && resp.item.merged
        ? "That English term already existed — your phrasings were added to it as alternates. Cache flushed."
        : editing.id
        ? "Term updated — cache flushed."
        : "Term added — cache flushed."
    );
    setEditing(null);
    search(0, false);
  };

  const removeTerm = async (row) => {
    const ok = window.confirm(
      `Delete this term?\n\nEN: ${row.term}\nKR: ${row.kr || "—"}\nRU: ${row.ru || "—"}`
    );
    if (!ok) return;
    const resp = await call("terms:delete", { id: row.id });
    if (resp.error) {
      tmToast("error", resp.error);
      return;
    }
    tmToast("success", "Term deleted — cache flushed.");
    setItems((prev) => prev.filter((x) => x.id !== row.id));
    setTotal((t) => Math.max(0, t - 1));
  };

  // ---------------- Missing tab ----------------
  const [mq, setMq] = useState("");
  const [missing, setMissing] = useState([]);
  const [mTotal, setMTotal] = useState(0);
  const [mLoading, setMLoading] = useState(false);
  const mReqRef = useRef(0);

  const loadMissing = useCallback(
    async (skip = 0, append = false) => {
      const my = ++mReqRef.current;
      setMLoading(true);
      const resp = await call("terms:missing", { q: mq, skip, limit: LIMIT });
      if (my !== mReqRef.current) return;
      setMLoading(false);
      if (resp.error) {
        tmToast("error", resp.error);
        return;
      }
      setMissing((prev) => (append ? [...prev, ...resp.items] : resp.items));
      setMTotal(resp.total);
    },
    [call, mq, tmToast]
  );

  useEffect(() => {
    if (tab !== "missing" || !isSocketReady) return undefined;
    const t = setTimeout(() => loadMissing(0, false), 250);
    return () => clearTimeout(t);
  }, [mq, tab, isSocketReady, loadMissing]);

  const dismissMissing = async (row) => {
    const resp = await call("terms:missingDelete", { id: row.id });
    if (resp.error) {
      tmToast("error", resp.error);
      return;
    }
    setMissing((prev) => prev.filter((x) => x.id !== row.id));
    setMTotal((t) => Math.max(0, t - 1));
  };

  const clearAllMissing = async () => {
    const scope = mq.trim() ? ` matching "${mq.trim()}"` : "";
    if (!window.confirm(`Clear ${mTotal} missing-term entr${mTotal === 1 ? "y" : "ies"}${scope}?`)) {
      return;
    }
    const resp = await call("terms:missingDelete", { all: true, q: mq });
    if (resp.error) {
      tmToast("error", resp.error);
      return;
    }
    tmToast("success", `Cleared ${resp.deleted} entr${resp.deleted === 1 ? "y" : "ies"}.`);
    loadMissing(0, false);
  };

  // Pre-fill the term editor from a missing entry (its text is already in the
  // normalized "$" form the translator looks up, which is the right stored form).
  const addFromMissing = (row) => {
    const e = emptyTerm();
    // Fall back to detecting the script if the entry has no language recorded.
    const lang =
      row.lang ||
      (/[가-힣]/.test(row.term) ? "kr" : /[а-яА-ЯёЁ]/.test(row.term) ? "ru" : "en");
    if (lang === "kr") e.kr = row.term;
    else if (lang === "ru") e.ru = row.term;
    else e.term = row.term;
    setEditing(e);
    setTab("terms");
  };

  // ---------------- Test tab ----------------
  const [tText, setTText] = useState("");
  const [tLang, setTLang] = useState("en");
  const [tRes, setTRes] = useState(null);
  const [tBusy, setTBusy] = useState(false);

  const runTest = async () => {
    if (!tText.trim()) return;
    setTBusy(true);
    const resp = await call("terms:test", { text: tText, targetLang: tLang });
    setTBusy(false);
    if (resp.error) {
      tmToast("error", resp.error);
      return;
    }
    setTRes(resp.lines);
  };

  // ---------------- render helpers ----------------
  const onEditKey = (e) => {
    // An Enter that merely commits an IME composition (Korean input) must not save.
    if (e.nativeEvent && (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229)) return;
    if (e.key === "Enter") {
      e.preventDefault();
      saveEdit();
    } else if (e.key === "Escape") {
      setEditing(null);
    }
  };

  // Alternate phrasings box: Enter adds a line, Ctrl/Cmd+Enter saves, Esc cancels.
  const onAltKey = (e) => {
    if (e.nativeEvent && (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229)) return;
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      saveEdit();
    } else if (e.key === "Escape") {
      setEditing(null);
    }
  };
  const renderAlts = (field) => (
    <textarea
      className="tm-alts"
      rows={Math.min(4, Math.max(1, (editing[field] || "").split("\n").length))}
      value={editing[field] || ""}
      onChange={(e) => setEditing({ ...editing, [field]: e.target.value })}
      onKeyDown={onAltKey}
      placeholder="Alternate phrasings — one per line"
      title="Other source phrasings that mean the same thing; each one matches this term. Ctrl+Enter saves."
    />
  );

  // Rendered via a function call (not as a nested component) so React keeps the
  // same <tr>/<input> nodes across re-renders — a nested component type would
  // remount the row on every keystroke and drop the input focus.
  const renderEditRow = (key) => (
    <tr key={key} className="tm-editrow">
      <td>
        <input
          autoFocus
          value={editing.term}
          onChange={(e) => setEditing({ ...editing, term: e.target.value })}
          onKeyDown={onEditKey}
          placeholder="English term — use $ as the value placeholder"
        />
      </td>
      <td>
        <input
          value={editing.kr}
          onChange={(e) => setEditing({ ...editing, kr: e.target.value })}
          onKeyDown={onEditKey}
          placeholder="Korean"
        />
        {renderAlts("krAltsText")}
      </td>
      <td>
        <input
          value={editing.ru}
          onChange={(e) => setEditing({ ...editing, ru: e.target.value })}
          onKeyDown={onEditKey}
          placeholder="Russian"
        />
        {renderAlts("ruAltsText")}
      </td>
      <td className="tm-actions">
        <button className="tm-btn tm-primary" onClick={saveEdit}>
          Save
        </button>
        <button className="tm-btn" onClick={() => setEditing(null)}>
          Cancel
        </button>
      </td>
    </tr>
  );

  return (
    <div>
      <NavBar username={username} onLogout={logout} updateDatabase={updateDatabase} />
      <div className="tm-page">
        <h2 className="tm-title">Term Database</h2>

        <div className="tm-tabs">
          {[
            ["terms", `Terms${tab === "terms" && total ? ` (${total})` : ""}`],
            ["missing", `Missing${tab === "missing" && mTotal ? ` (${mTotal})` : ""}`],
            ["test", "Test a line"],
          ].map(([k, label]) => (
            <button
              key={k}
              className={`tm-tab${tab === k ? " tm-tab-on" : ""}`}
              onClick={() => setTab(k)}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "terms" && (
          <>
            <div className="tm-bar">
              <input
                className="tm-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search terms…"
              />
              <select value={field} onChange={(e) => setField(e.target.value)}>
                {FIELD_OPTS.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              <button
                className="tm-btn tm-primary"
                onClick={() => setEditing(emptyTerm())}
                disabled={!!editing && !editing.id}
              >
                + New term
              </button>
              <span className="tm-meta">
                {loading ? "Searching…" : `${items.length} of ${total} shown`}
              </span>
            </div>
            <p className="tm-hint">
              <b>$</b> marks a value placeholder (number, %, or <code>$s1</code>-style
              variable). Inside a sentence put spaces around it — <code>최대 $ 명 공격</code>;
              at the end just <code>방어 무시 $</code>. Every save flushes the translator cache.
              A term can carry several source phrasings that mean the same thing: put the extra
              Korean / Russian lines in the <b>Alternate phrasings</b> box (one per line) — each of
              them translates to this English. Saving a new term whose English already exists
              adds its phrasings to that term instead. When the values come in a different order
              than in the English, number the slots in the Korean / Russian phrasing after the
              English order: <code>… $2 위력으로 $1 회 공격</code> for{" "}
              <code>Attacks the target $ times. Power $.</code>. A phrasing that spells a value
              out (<code>… 2회 공격</code> → "twice") wins over the <code>$</code> one for that value.
            </p>
            <div className="tm-gridwrap">
              <table className="tm-grid">
                <thead>
                  <tr>
                    <th>English term</th>
                    <th>Korean</th>
                    <th>Russian</th>
                    <th className="tm-actions-h">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* New term, or an edit whose row has scrolled out of the current
                      results — keep it visible at the top instead of orphaning it. */}
                  {editing &&
                    (!editing.id || !items.some((r) => r.id === editing.id)) &&
                    renderEditRow(editing.id || "__new")}
                  {items.map((row) =>
                    editing && editing.id === row.id ? (
                      renderEditRow(row.id)
                    ) : (
                      <tr key={row.id}>
                        <td className="tm-cell">{row.term}</td>
                        <td className="tm-cell">
                          {row.kr || <span className="tm-empty">—</span>}
                          {row.krCount ? <span className="tm-count">{row.krCount}</span> : null}
                          {(row.krAlts || []).map((a) => (
                            <div className="tm-alt" key={a}>{a}</div>
                          ))}
                        </td>
                        <td className="tm-cell">
                          {row.ru || <span className="tm-empty">—</span>}
                          {row.ruCount ? <span className="tm-count">{row.ruCount}</span> : null}
                          {(row.ruAlts || []).map((a) => (
                            <div className="tm-alt" key={a}>{a}</div>
                          ))}
                        </td>
                        <td className="tm-actions">
                          <button className="tm-btn" onClick={() => setEditing(toEdit(row))}>
                            Edit
                          </button>
                          <button className="tm-btn tm-danger" onClick={() => removeTerm(row)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                  {!loading && items.length === 0 && !editing && (
                    <tr>
                      <td colSpan={4} className="tm-empty-row">
                        No terms match.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {items.length < total && (
              <button className="tm-btn tm-more" onClick={() => search(items.length, true)}>
                Load more ({total - items.length} left)
              </button>
            )}
          </>
        )}

        {tab === "missing" && (
          <>
            <div className="tm-bar">
              <input
                className="tm-search"
                value={mq}
                onChange={(e) => setMq(e.target.value)}
                placeholder="Search missing terms…"
              />
              <button
                className="tm-btn tm-danger"
                onClick={clearAllMissing}
                disabled={!mTotal}
              >
                Clear all
              </button>
              <span className="tm-meta">
                {mLoading ? "Loading…" : `${missing.length} of ${mTotal} shown`}
              </span>
            </div>
            <p className="tm-hint">
              Lines the auto-translator could not match, in the normalized form it looked
              up (newest first). <b>Add as term</b> pre-fills the editor with that text.
            </p>
            <div className="tm-gridwrap">
              <table className="tm-grid">
                <thead>
                  <tr>
                    <th>Looked-up text</th>
                    <th>Lang</th>
                    <th className="tm-actions-h">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {missing.map((row) => (
                    <tr key={row.id}>
                      <td className="tm-cell">{row.term}</td>
                      <td className="tm-cell tm-lang">{LANG_NAME[row.lang] || row.lang || "—"}</td>
                      <td className="tm-actions">
                        <button className="tm-btn tm-primary" onClick={() => addFromMissing(row)}>
                          Add as term
                        </button>
                        <button className="tm-btn" onClick={() => dismissMissing(row)}>
                          Dismiss
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!mLoading && missing.length === 0 && (
                    <tr>
                      <td colSpan={3} className="tm-empty-row">
                        Nothing missing — every looked-up line matched a term.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {missing.length < mTotal && (
              <button className="tm-btn tm-more" onClick={() => loadMissing(missing.length, true)}>
                Load more ({mTotal - missing.length} left)
              </button>
            )}
          </>
        )}

        {tab === "test" && (
          <>
            <p className="tm-hint">
              Paste a description (literal <code>\n</code> line breaks are fine) and see, per
              line, the <b>lookup key</b> the translator used and what it produced.
            </p>
            <textarea
              className="tm-textarea"
              value={tText}
              onChange={(e) => setTText(e.target.value)}
              placeholder={"방어 무시 10%%\\nPVE: 최대 5명 공격"}
            />
            <div className="tm-bar">
              <label className="tm-inline">
                Translate to&nbsp;
                <select value={tLang} onChange={(e) => setTLang(e.target.value)}>
                  <option value="en">English</option>
                  <option value="ru">Russian</option>
                  <option value="kr">Korean</option>
                </select>
              </label>
              <button className="tm-btn tm-primary" onClick={runTest} disabled={tBusy}>
                {tBusy ? "Running…" : "Run"}
              </button>
            </div>
            {tRes && (
              <div className="tm-gridwrap">
                <table className="tm-grid">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Source line</th>
                      <th>Lookup key</th>
                      <th>Result</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tRes.map((l, i) => (
                      <tr key={i} className={l.empty ? "tm-row-dim" : l.translated ? "" : "tm-row-miss"}>
                        <td className="tm-cell tm-num">{i + 1}</td>
                        <td className="tm-cell">{l.line || <span className="tm-empty">(blank)</span>}</td>
                        <td className="tm-cell tm-key">{l.key}</td>
                        <td className="tm-cell">
                          {l.out}
                          {l.hint ? <div className="tm-testhint">{l.hint}</div> : null}
                        </td>
                        <td className="tm-cell">
                          {l.empty ? "" : l.translated ? "✓ translated" : "✗ no match"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Terms;
